import { observable } from '@legendapp/state';
import { syncedSupabase } from '@legendapp/state/sync-plugins/supabase';
import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../lib/supabase';
import { auth$ } from './auth$';
import { ObservablePersistLocalStorage } from '@legendapp/state/persist-plugins/local-storage';
import { createSyncCreate, createSyncUpdate, defaultSyncConfig } from './syncHelpers';
import { trackEvent } from '../lib/analytics';

// Reading progress store with Supabase sync (local-first)
// Key: composite of article_id and user_id for unique identification
export const readingProgress$ = observable(syncedSupabase({
  supabase,
  collection: 'reedle_reading_progress',
  // Filter by current user
  filter: (select) => {
    const userId = auth$.user.get()?.id;
    return userId ? select.eq('user_id', userId) : select;
  },
  // Custom create/update with error handling
  // Use upsert directly with composite constraint to avoid 409 errors when record exists in DB but not locally
  create: createSyncCreate('reedle_reading_progress', {
    onConflict: 'article_id,user_id',
    useUpsert: true,
  }),
  update: createSyncUpdate('reedle_reading_progress'),
  // Use default sync config
  ...defaultSyncConfig,
  // Debounce network writes only; local progress and persistence stay immediate.
  debounceSet: 500,
  // Persist data locally
  persist: {
    name: 'reedle-reading-progress',
    plugin: ObservablePersistLocalStorage,
    retrySync: true,
  },
}));

/**
 * Get or create progress entry for an article
 * Uses article_id as the key since user_id is implicit from filter
 */
export const getProgress = (articleId) => {
  const progressObj = readingProgress$.get() || {};
  // Find the progress entry for this article
  const entry = Object.values(progressObj).find(p => p?.article_id === articleId);
  return entry || null;
};

/**
 * Get progress ID for an article (for direct updates)
 */
export const getProgressId = (articleId) => {
  const progressObj = readingProgress$.get() || {};
  for (const [id, progress] of Object.entries(progressObj)) {
    if (progress?.article_id === articleId) {
      return id;
    }
  }
  return null;
};

/**
 * Update reading progress (offline-capable)
 */
export const updateProgress = (articleId, readIndices, totalBlocks) => {
  const userId = auth$.user.get()?.id;
  if (!userId) return;

  let progressId = getProgressId(articleId);

  if (progressId) {
    // Update existing
    readingProgress$[progressId].assign({
      read_indices: readIndices,
      total_blocks: totalBlocks,
      last_updated: new Date().toISOString(),
    });
  } else {
    // Create new
    progressId = uuidv4();
    readingProgress$[progressId].set({
      id: progressId,
      article_id: articleId,
      user_id: userId,
      read_indices: readIndices,
      total_blocks: totalBlocks,
      last_updated: new Date().toISOString(),
    });
  }
};

/**
 * Get read indices for an article
 */
export const getReadIndices = (articleId) => {
  const progress = getProgress(articleId);
  return progress?.read_indices || [];
};

/**
 * Get total blocks for an article
 */
export const getTotalBlocks = (articleId) => {
  const progress = getProgress(articleId);
  return progress?.total_blocks || 0;
};

/**
 * Get hidden block indices for an article (soft delete)
 */
export const getHiddenBlocks = (articleId) => {
  const progress = getProgress(articleId);
  return progress?.hidden_block_indices || [];
};

/**
 * Update hidden block indices (soft delete blocks)
 * @param {string} articleId - Article ID
 * @param {number[]} hiddenIndices - Array of block indices to hide
 */
export const updateHiddenBlocks = (articleId, hiddenIndices) => {
  const userId = auth$.user.get()?.id;
  if (!userId) return;

  let progressId = getProgressId(articleId);

  if (progressId) {
    // Update existing
    readingProgress$[progressId].assign({
      hidden_block_indices: hiddenIndices,
      last_updated: new Date().toISOString(),
    });
  } else {
    // Create new progress entry with hidden blocks
    progressId = uuidv4();
    readingProgress$[progressId].set({
      id: progressId,
      article_id: articleId,
      user_id: userId,
      read_indices: [],
      total_blocks: 0,
      hidden_block_indices: hiddenIndices,
      last_updated: new Date().toISOString(),
    });
  }
};

/**
 * Get whether an article is archived (per-user)
 */
/**
 * "Almost done" means half-read but not finished, and still in play.
 *
 * The Dashboard shelf and the phone empty-inbox escape both offer unfinished
 * reading, so the threshold that decides what counts lives here rather than in
 * whichever surface happens to render it.
 */
export const isAlmostDoneProgress = (progress) => {
  if (!progress || !progress.article_id || progress.is_archived) return false;
  const total = progress.total_blocks || 0;
  if (total === 0) return false;
  const readCount = Array.isArray(progress.read_indices) ? progress.read_indices.length : 0;
  const ratio = readCount / total;
  return ratio >= 0.5 && ratio < 1.0;
};

/** How many articles are unfinished, for a count chip. */
export const getAlmostDoneCount = () => Object
  .values(readingProgress$.get() || {})
  .filter(isAlmostDoneProgress)
  .length;

export const getIsArchived = (articleId) => {
  const progress = getProgress(articleId);
  return progress?.is_archived === true;
};

/**
 * Toggle archive status for an article (per-user)
 */
export const toggleArchive = (articleId) => {
  const userId = auth$.user.get()?.id;
  if (!userId) return;

  let progressId = getProgressId(articleId);
  const now = new Date().toISOString();

  let newValue;
  if (progressId) {
    const current = readingProgress$[progressId].get();
    newValue = !current?.is_archived;
    readingProgress$[progressId].assign({
      is_archived: newValue,
      last_updated: now,
    });
  } else {
    // Create new progress entry with archived=true
    newValue = true;
    progressId = uuidv4();
    readingProgress$[progressId].set({
      id: progressId,
      article_id: articleId,
      user_id: userId,
      read_indices: [],
      total_blocks: 0,
      is_archived: true,
      last_updated: now,
    });
  }
  trackEvent('article_archived', { article_id: articleId, is_archived: newValue });
};

/**
 * Set archive status for an article (per-user)
 */
export const setArchived = (articleId, value) => {
  const userId = auth$.user.get()?.id;
  if (!userId) return;

  let progressId = getProgressId(articleId);
  const now = new Date().toISOString();

  if (progressId) {
    readingProgress$[progressId].assign({
      is_archived: value,
      last_updated: now,
    });
  } else {
    progressId = uuidv4();
    readingProgress$[progressId].set({
      id: progressId,
      article_id: articleId,
      user_id: userId,
      read_indices: [],
      total_blocks: 0,
      is_archived: value,
      last_updated: now,
    });
  }
  trackEvent('article_archived', { article_id: articleId, is_archived: value });
};
